import { hasLocale, Locale } from "next-intl";

import { isDefaultLocale, routing } from "./routing";

const prefixPath = (path: string, locale: string): string => (
  path === "/" ? `/${locale}` : `/${locale}${path}`
);

/**
 * Splits a leading locale segment off a pathname
 * e.g. `/de/customer` -> `{ locale: "de", pathname: "/customer" }`
 * Pathnames without a locale segment resolve to the default locale unchanged.
 */
export const stripLocalePrefix = (pathname: string): { locale: Locale; pathname: string } => {
  const [ , firstSegment, ...rest ] = pathname.split("/");

  if (!hasLocale(routing.locales, firstSegment)) {
    return { locale: routing.defaultLocale, pathname };
  }

  return { locale: firstSegment, pathname: `/${rest.join("/")}` };
};

/**
 * Route maps and path checks are written without a locale,
 * so `/de/customer` is matched as `/customer`
 */
export const getPathnameWithoutLocale = (pathname: string): string => stripLocalePrefix(pathname).pathname;

/** Adds the public locale prefix to a path; the default locale stays unprefixed. */
export const withLocalePrefix = (path: string, locale: string): string => (
  isDefaultLocale(locale) ? path : prefixPath(path, locale)
);

/**
 * Puts an unprefixed path on the same locale as the current pathname,
 * so a `/de` visitor stays on `/de`. Absolute URLs are returned untouched.
 */
export const localisePath = (path: string, currentPathname: string): string => {
  if (!path.startsWith("/")) {
    return path;
  }

  return withLocalePrefix(path, stripLocalePrefix(currentPathname).locale);
};

/**
 * Returns the internal `/[locale]/...` path for every locale.
 * Pages are cached under these rewritten paths, so revalidation
 * has to target them rather than the public URL.
 */
export const getLocalisedPaths = (path: string): string[] => {
  const { pathname } = stripLocalePrefix(path);
  return routing.locales.map((locale) => prefixPath(pathname, locale));
};

/** Routes that live outside `app/[locale]`, so are cached under their public path. */
const UNLOCALISED_PATHS = new Set([ "/sitemap.xml", "/robots.txt" ]);

/** Returns the internal paths `revalidatePath` needs to refresh every cached copy of a public path. */
export const getRevalidationPaths = (path: string): string[] => (
  UNLOCALISED_PATHS.has(path) ? [ path ] : getLocalisedPaths(path)
);
